import React, { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

const distributionPatterns = [
  { 
    id: 'front_loaded', 
    title: 'Front Loaded', 
    description: 'More work at the beginning (50%, 30%, 20%)',
    visual: [
      { width: '50%', height: '24px', bg: 'bg-indigo-600' },
      { width: '30%', height: '24px', bg: 'bg-indigo-400' },
      { width: '20%', height: '24px', bg: 'bg-indigo-200' }
    ]
  },
  { 
    id: 'even', 
    title: 'Even Distribution', 
    description: 'Work spread evenly (34%, 33%, 33%)',
    visual: [
      { width: '34%', height: '24px', bg: 'bg-indigo-400' },
      { width: '33%', height: '24px', bg: 'bg-indigo-400' },
      { width: '33%', height: '24px', bg: 'bg-indigo-400' }
    ]
  },
  { 
    id: 'back_loaded', 
    title: 'Back Loaded', 
    description: 'More work at the end (20%, 30%, 50%)',
    visual: [
      { width: '20%', height: '24px', bg: 'bg-indigo-200' },
      { width: '30%', height: '24px', bg: 'bg-indigo-400' },
      { width: '50%', height: '24px', bg: 'bg-indigo-600' }
    ]
  },
  { 
    id: 'front_heavy', 
    title: 'Front Heavy', 
    description: 'Most work at the beginning (70%, 20%, 10%)',
    visual: [
      { width: '70%', height: '24px', bg: 'bg-indigo-700' },
      { width: '20%', height: '24px', bg: 'bg-indigo-300' },
      { width: '10%', height: '24px', bg: 'bg-indigo-100' }
    ]
  },
  { 
    id: 'back_heavy', 
    title: 'Back Heavy', 
    description: 'Most work at the end (10%, 20%, 70%)',
    visual: [
      { width: '10%', height: '24px', bg: 'bg-indigo-100' },
      { width: '20%', height: '24px', bg: 'bg-indigo-300' },
      { width: '70%', height: '24px', bg: 'bg-indigo-700' }
    ]
  }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function DistributionSelector({ value, shortName, onChangeSuccess }) {
  const [selectedPattern, setSelectedPattern] = useState(
    distributionPatterns.find(pattern => pattern.id === value) || distributionPatterns[0]
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const handleDistributionChange = async (newPattern) => {
    if (newPattern.id === selectedPattern.id) return;
    
    setSelectedPattern(newPattern);
    setIsUpdating(true);
    setError(null);
    
    try {
      // Import dynamically to avoid circular dependencies
      const { updateScopeField } = await import('../../utils/api');
      
      // Call API to update the distribution
      const result = await updateScopeField(shortName, 'distribution', newPattern.id);
      
      if (result.success) {
        // Update was successful
        console.log(result.message);
        if (onChangeSuccess) {
          await onChangeSuccess();
        }
      } else {
        // Update failed
        setError('Failed to update distribution pattern');
        // Revert to previous selection
        setSelectedPattern(
          distributionPatterns.find(pattern => pattern.id === value) || distributionPatterns[0]
        );
      }
    } catch (error) {
      console.error('Error updating distribution pattern:', error);
      setError(`Error: ${error.message}`);
      // Revert to previous selection
      setSelectedPattern(
        distributionPatterns.find(pattern => pattern.id === value) || distributionPatterns[0]
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      <RadioGroup
        value={selectedPattern}
        onChange={handleDistributionChange}
        className="mt-2 grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-4"
      >
        {distributionPatterns.map((pattern) => (
          <RadioGroup.Option
            key={pattern.id}
            value={pattern}
            disabled={isUpdating}
            className={({ checked, active }) =>
              classNames(
                checked ? 'border-indigo-600' : 'border-gray-300',
                active ? 'border-indigo-600 ring-2 ring-indigo-600' : '',
                isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                'relative flex rounded-lg border bg-white p-4 shadow-sm focus:outline-none'
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1 flex-col">
                  <RadioGroup.Label as="span" className="block text-sm font-medium text-gray-900">
                    {pattern.title}
                  </RadioGroup.Label>
                  <RadioGroup.Description as="span" className="mt-1 flex text-xs text-gray-500">
                    {pattern.description}
                  </RadioGroup.Description>
                  <span className="mt-3 flex flex-row w-full">
                    {pattern.visual.map((bar, index) => (
                      <div 
                        key={index} 
                        className={`${bar.bg} h-2 rounded-sm`} 
                        style={{ width: bar.width }}
                      ></div>
                    ))}
                  </span>
                </span>
                <CheckCircleIcon
                  className={classNames(
                    !checked ? 'invisible' : '',
                    'h-5 w-5 text-indigo-600'
                  )}
                  aria-hidden="true"
                />
                <span
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-indigo-600' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg'
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
      
      {isUpdating && (
        <p className="mt-2 text-xs text-gray-500 animate-pulse">
          Updating distribution pattern...
        </p>
      )}
      
      {error && (
        <p className="mt-2 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}